import { computed, toRaw, watch } from 'vue';

import VALIDATORS from '@validators/all';

import { BANK_CODES } from '@js/constants/banks';

import RULES from '../javascripts/rules';
import optionize, { sort, booleanSort, BOOLEANS } from '../javascripts/optionize';
import filled from '../javascripts/filled';
import fieldName, { cloneName } from '../javascripts/name';

import { VISIBLE_OPTIONS_FALSE } from '../javascripts/options';

const BLANK = VALIDATORS.blank.message();

export default function propertier(props, planter, name) {
  const {
    leaves,
    values,
    parents,
    properties,
    errors,
    visibilities,
    readonlies,
  } = planter;

  const key = computed(_key);
  const options = computed(_options);
  const optionized = computed(_optionized);
  const type = computed(_type);
  const required = computed(_required);
  const maxlength = computed(_maxlength);
  const combined = computed(_combined);
  const combining = computed(_combining);
  const destroyer = computed(_destroyer);
  const destroyed = computed(_destroyed);
  const constant = computed(_constant);
  const hidden = computed(_hidden);
  const rules = computed(_rules);
  const overruled = computed(_overruled);

  function _key() {
    return name.split('[').pop().replace(/]/, '');
  }

  function _required() {
    return leaves.value[name].validations?.some((v) => v.type === 'blank');
  }

  function _maxlength() {
    return leaves.value[name].validations?.find((i) => i.type === 'too_long')?.count
      || (leaves.value[name].mask?.bigFormat
      || leaves.value[name].mask?.format)?.length;
  }

  function _options() {
    let sorter;

    let list = (
      leaves.value[name].options
      || props.domains[leaves.value[name].domain]
    )?.map(optionize);

    const filter = props.filters[name] || props.filters[cloneName(name)];

    if (filter) {
      list = list.map(filter(props, planter, name).map);
    }

    if (list?.every((i) => BOOLEANS.includes(i.value))) {
      sorter = booleanSort;
    }

    return list?.sort(sorter || props.sorts[name] || props.sorts[cloneName(name)] || sort);
  }

  function _optionized() {
    return Array.isArray(toRaw(options.value));
  }

  function _type() {
    const { type: __type } = leaves.value[name];

    if (__type) {
      if (__type !== 'search' || props.inlines[name]) {
        return __type;
      }
    }

    if (optionized.value) {
      if (options.value.length && options.value.every((i) => BOOLEANS.includes(i.value))) {
        return 'boolean';
      }

      return 'select';
    }

    if (leaves.value[name].validations?.some((v) => v.type === 'file')) {
      return 'file';
    }

    return leaves.value[name].mask ? 'tel' : 'text';
  }

  function _combined() {
    return props.combine[parents.value[name]]
      ?.combine.includes(key.value);
  }

  function _combining() {
    return props.combine[parents.value[name]]
      ?.combine.map((i) => fieldName(parents.value[name], i));
  }

  function _destroyer() {
    let _name = name.split('[');
    _name.pop();
    _name.push('_destroy]');
    _name = _name.join('[');

    return leaves.value[_name] ? _name : null;
  }

  function _destroyed() {
    return [true, 'true'].includes(values.value[destroyer.value]);
  }

  function _constant() {
    const value = leaves.value[name].validations?.find((v) => v.type === 'constant')?.value;

    if (filled(value)) {
      const string = String(value);

      if (leaves.value[name].domain) {
        const found = props.domains[leaves.value[name].domain]?.find((i) => [
          String(i.id),
          String(i.name),
          String(i.identifier),
        ].includes(string));

        return found?.id || found?.identifier;
      }

      return value;
    }

    return undefined;
  }

  function _rules() {
    const unknown = props.banks.some((code) => !BANK_CODES.includes(code));
    const keys = Object.keys(leaves.value[name].rules || {});

    return keys
      .filter((code) => unknown || !keys.length || props.banks.includes(code))
      .reduce((reduced, code) => {
        reduced[code] = leaves.value[name].rules[code];

        return reduced;
      }, {});
  }

  function _overruled() {
    function iterate(rule, operation) {
      if (Array.isArray(rule)) {
        return rule[operation === 'or' ? 'some' : 'every']((i) => iterate(i, 'and'));
      }

      return RULES[rule.rule](
        values.value[rule.field],
        rule,
        properties.value[rule.field]?.options,
      );
    }

    return !Object
      .values(rules.value)
      .some((i) => iterate(i, 'or'));
  }

  function _hidden() {
    return !errors.value[name]
      && (!readonlies.value[name] || visibilities.value[name] === VISIBLE_OPTIONS_FALSE)
      && !!(
        false
        || visibilities.value[name] === VISIBLE_OPTIONS_FALSE
        || combined.value
        || filled(constant.value)
        || overruled.value
      );
  }

  function overhide() {
    if (hidden.value || hidden.value === overruled.value) {
      return;
    }

    if (errors.value[name] === BLANK) {
      errors.value[name] = '';
    }
  }

  watch(overruled, overhide);

  return {
    key,
    overruled,
    type,
    required,
    maxlength,
    options,
    optionized,
    combined,
    combining,
    constant,
    destroyer,
    destroyed,
    hidden,
    rules,
  };
}
